import React, { Fragment, useCallback, useMemo } from 'react';
import T from 'prop-types';

import SdkFunctionsDropdown from '../../../_shared/components/SdkFunctionsDropdown';
import SwitchStores from '../../../../../components/SwitchStores';
import SingleDatePicker from '../../../../../components/SingleDatePicker';
import CountriesDropdown from '../../../../../components/CountriesDropdown';
import CategoriesSingleSelect from '../../../../../components/CategoriesSingleSelect';
import SharingSocials from './SharingSocials';

import helpers from '../../../../../utils/helpers';
import { format, yesterday } from '../../../../../utils/date';

const TopChartsFilters = ({
  onFilterChange,
  filters: { data, store, category_id, country_iso, function_id },
  enableTopFilters = ['store'],
  shareOptions,
}) => {
  const store_id = store;
  const date = data && data.free && data.free.date;

  const selectedCategory = useMemo(
    () => [
      {
        id: category_id,
        value: category_id,
        store: store_id,
        label: helpers.categoryNameForId(category_id, store_id),
      },
    ],
    [category_id, store_id]
  );

  const handleStoreChange = useCallback((value) => {
    onFilterChange({ store: value });
  }, []);

  const handleDateChange = useCallback((value) => {
    const dateValue = value || yesterday();
    onFilterChange({ date: dateValue });
  }, []);

  const handleCategoryChange = useCallback(
    ([category]) => {
      if (category.id !== category_id) {
        onFilterChange({ category: category.id });
      }
    },
    [category_id]
  );

  const handleCountryChange = useCallback((value) => {
    onFilterChange({ country: value });
  }, []);

  const handleFunctionsChange = useCallback((value, label) => {
    const params = { function_id: value, function: label };
    onFilterChange(params);
  }, []);
  const sdkFunction = function_id || 'all';

  const renderComponentByFilterType = (filterType) => {
    switch (filterType) {
      case 'store':
        return (
          <li className="filter">
            <div className="filter-label">Store</div>
            <div className="filter-content">
              <SwitchStores size="lg" active={store_id} onChange={handleStoreChange} />
            </div>
          </li>
        );
      case 'single_date_picker':
        return (
          <li className="filter">
            <div className="filter-label">Date</div>
            <div className="filter-content">
              <SingleDatePicker
                maxPeriod={31}
                inputValue={format(date) || yesterday()}
                inputDisabled={false}
                overlayPlacement="bottom-right"
                withCalendarIcon
                onDateChange={handleDateChange}
              />
            </div>
          </li>
        );
      case 'countries':
        return (
          <li className="filter">
            <div className="filter-label">Country</div>
            <div className="filter-content">
              <CountriesDropdown
                selected={country_iso}
                size="lg"
                menuAlign="start"
                position="initial"
                menuDirection="bottom"
                onChange={handleCountryChange}
              />
            </div>
          </li>
        );
      case 'categories':
        return (
          <li className="filter">
            <div className="filter-label">Category</div>
            <div className="filter-content">
              <CategoriesSingleSelect
                store={store_id}
                categories={selectedCategory}
                multi={false}
                showAsDropdown
                className="categories-dropdown"
                nestedGroupPosition="left"
                enableUmbrellaCategories
                onChange={handleCategoryChange}
              />
            </div>
          </li>
        );
      case 'sdks_functions':
        return (
          <li className="filter">
            <div className="filter-label">SDK Function</div>
            <div className="filter-content">
              <SdkFunctionsDropdown
                active={sdkFunction}
                size="lg"
                onChange={handleFunctionsChange}
              />
            </div>
          </li>
        );
      default:
        return undefined;
    }
  };

  return (
    <div className="filter-group pos-rel prospect-charts-filters" id="reports-filter">
      <div className="row">
        <div className="col-xxs-12 col-md-9 d-flex justify-content-center mx-auto justify-content-md-start">
          <ul className="d-flex flex-items-xxs-center flex-wrap p-t-xxs p-b-sm">
            {enableTopFilters.map((filter) => (
              <Fragment key={filter}>{renderComponentByFilterType(filter)}</Fragment>
            ))}
          </ul>
        </div>
        <SharingSocials shareOptions={shareOptions} />
      </div>
    </div>
  );
};

TopChartsFilters.propTypes = {
  onFilterChange: T.func.isRequired,
  filters: T.object.isRequired,
  enableTopFilters: T.arrayOf(
    T.oneOf(['store', 'single_date_picker', 'categories', 'countries', 'sdks_functions'])
  ),
  enableBottomFilters: T.arrayOf(T.oneOf(['subscribe_to_alerts'])),
  permissions: T.object,
  shareOptions: T.object,
};

export default TopChartsFilters;
